import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, MessageSquare, CheckCircle2, Info } from 'lucide-react';

export const FreeStrategyCallPage = () => {
  const whatToExpect = [
    'Discuss your painting goals and vision',
    'Review project scope and timeline',
    'Get expert advice on colors and finishes',
    'Understand our process and what to expect',
    'Receive a preliminary cost estimate',
    'Ask any questions about your project'
  ];

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="free-strategy-call-page">
      {/* Header */}
      <section className="py-12 sm:py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase mb-4">
              Book Your Free <span className="text-amber-500">Strategy Call</span>
            </h1>
            <p className="text-slate-400 text-lg">
              Schedule a complimentary consultation with our painting experts. No pressure, no obligations 
              - just helpful guidance for your project.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 sm:py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-6">
                  What to Expect
                </h2>
                <p className="text-slate-400 mb-6">
                  During your free 15-20 minute strategy call, we'll discuss your painting project 
                  and help you understand the best approach for your home.
                </p>

                <ul className="space-y-4">
                  {whatToExpect.map((item) => (
                    <li key={item.replace(/\s+/g, '-').toLowerCase().slice(0, 30)} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Options */}
              <div className="bg-slate-900 border border-slate-800 p-6">
                <h3 className="font-heading font-bold text-xl text-white uppercase mb-4">
                  Prefer to Call?
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  You can also reach us directly to schedule your consultation.
                </p>
                <div className="space-y-3">
                  <a
                    href="tel:+18303270136"
                    className="flex items-center gap-3 text-amber-500 hover:text-amber-400 font-semibold transition-colors"
                    data-testid="call-phone"
                  >
                    <Phone className="w-5 h-5" />
                    (830) 327-0136
                  </a>
                  <a
                    href="mailto:matthew@propainting.cloud"
                    className="flex items-center gap-3 text-slate-400 hover:text-amber-500 transition-colors"
                    data-testid="call-email"
                  >
                    <MessageSquare className="w-5 h-5" />
                    matthew@propainting.cloud
                  </a>
                </div>
              </div>

              {/* SMS Notice */}
              <div className="bg-cyan-500/10 border border-cyan-500/30 p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                <p className="text-slate-400 text-sm">
                  If you opt in to SMS on our contact form, you may receive appointment reminders via text. 
                  Reply STOP to opt out at any time.
                </p>
              </div>
            </div>

            {/* Right Column - Calendar Embed */}
            <div>
              <div className="bg-slate-900 border border-slate-800 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="w-6 h-6 text-amber-500" />
                  <h2 className="font-heading font-bold text-2xl text-white uppercase">
                    Schedule Your Call
                  </h2>
                </div>

                {/* GoHighLevel Calendar Embed */}
                <div className="bg-slate-950 border border-slate-800 min-h-[600px] overflow-auto" data-testid="calendar-embed">
                  <iframe
                    src="https://links.roxvegas.cloud/widget/booking/MPgsTReP28k9asmJN5lw"
                    style={{ 
                      width: '100%', 
                      height: '700px',
                      border: 'none'
                    }}
                    scrolling="yes"
                    title="Pro Painting - Book a Strategy Call"
                  ></iframe>
                </div>

                {/* Alternative: Google Calendar Fallback */}
                {/* 
                <div className="mt-6 pt-6 border-t border-slate-800">
                  <p className="text-slate-500 text-sm text-center mb-4">
                    Alternative booking option:
                  </p>
                  <div className="bg-slate-950 border border-slate-800 p-8 text-center">
                    <p className="text-slate-400 text-sm">
                      PASTE GOOGLE APPOINTMENT SCHEDULE IFRAME HERE (OPTIONAL)
                    </p>
                  </div>
                </div>
                */}
              </div>

              {/* Additional CTA */}
              <div className="mt-6 text-center">
                <p className="text-slate-500 text-sm mb-4">
                  Want to provide more details first?
                </p>
                <Link
                  to="/contact"
                  className="inline-block border border-slate-600 text-white font-bold uppercase tracking-wider px-6 py-3 hover:border-amber-500 hover:text-amber-500 transition-all"
                  data-testid="link-to-contact"
                >
                  Fill Out Contact Form
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeStrategyCallPage;
